import { Button } from '@ooh/ui/components/button';
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from '@ooh/ui/components/tooltip';

interface AreaSelectionSummaryCardProps {
  selectedCount: number;
  visibleSelectedCount?: number;
  selectedIds: ReadonlySet<string | number>;
  /** When omitted (e.g. caller lacks `offer.update`), the add-to-offer button is hidden. */
  onAddToDraft?: (ids: (string | number)[]) => void;
  onClearSelection: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
  /** Optional callback to trigger bulk-download for area-selected IDs */
  onBulkDownloadSpecs?: (ids: number[]) => void;
  /** When true, the bulk-download button is rendered in a disabled state */
  bulkDownloadDisabled?: boolean;
  /** Tooltip text shown when the bulk-download button is disabled */
  bulkDownloadDisabledReason?: string;
}

export function AreaSelectionSummaryCard({
  selectedCount,
  visibleSelectedCount,
  selectedIds,
  onAddToDraft,
  onClearSelection,
  t,
  onBulkDownloadSpecs,
  bulkDownloadDisabled = false,
  bulkDownloadDisabledReason,
}: AreaSelectionSummaryCardProps) {
  if (selectedCount === 0) return null;

  const showDiscrepancy =
    visibleSelectedCount !== undefined && visibleSelectedCount < selectedCount;

  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-border bg-background/95 px-4 py-2.5 shadow-lg backdrop-blur-sm"
      data-testid="area-selection-summary-card"
    >
      <div className="flex flex-col">
        <span className="text-[13px] font-semibold text-foreground">
          {t('areaSelection.selectionCard.selectedCount', { count: selectedCount })}
        </span>
        {showDiscrepancy ? (
          <span className="text-[11px] text-muted-foreground">
            {t('areaSelection.selectionCard.visibleCount', { count: visibleSelectedCount })}
          </span>
        ) : null}
      </div>
      {onAddToDraft ? (
        <Button
          size="xs"
          variant="outline"
          onClick={() => onAddToDraft([...selectedIds])}
          data-testid="add-to-draft-btn"
        >
          {t('pinPopup.quickAdd')}
        </Button>
      ) : null}
      <Button
        size="xs"
        variant="ghost"
        onClick={onClearSelection}
        data-testid="clear-selection-btn"
      >
        {t('areaSelection.selectionCard.clearSelection')}
      </Button>
      {onBulkDownloadSpecs ? (
        <TooltipProvider delayDuration={400}>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* span wrapper required: disabled buttons don't fire pointer events */}
              <span tabIndex={bulkDownloadDisabled ? 0 : undefined}>
                <Button
                  size="xs"
                  variant="outline"
                  data-testid="area-bulk-download-trigger"
                  disabled={bulkDownloadDisabled}
                  onClick={() => {
                    // Cast string | number IDs to number[] — DB IDs are always numeric
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onBulkDownloadSpecs([...selectedIds].map(Number));
                  }}
                >
                  {t('areaSelection.selectionCard.bulkDownload')}
                </Button>
              </span>
            </TooltipTrigger>
            {bulkDownloadDisabled && bulkDownloadDisabledReason ? (
              <TooltipContent>{bulkDownloadDisabledReason}</TooltipContent>
            ) : null}
          </Tooltip>
        </TooltipProvider>
      ) : null}
    </div>
  );
}
